import SpinningStar from "../celebrate/SpinningStar";
import React, { useState } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { resolveEducatorUid } from "../../utils/resolveEducatorUid";
import SingAlongPlayer from "../singalong/SingAlongPlayer";
import { awardFirstPlayCoins } from "../../services/coinService";

// SING-ALONG
// ----------
// When the popup hands in a published lyricSync for this exact song file
// (PortalMediaPopup checks that), the plain audio bar is replaced by the
// sing-along player. Everything else is unchanged: the song ending still opens
// the "You've earned your Music Star!" popup, and Continue still records the
// play in userData and the teacher's classroom mirror.
const MusicTool = ({ musicUrl, subjectKey, slug, onProgressUpdate, lyricSync, title }) => {
  const [showPopup, setShowPopup] = useState(false);
  const db = getFirestore();

  // A) write to userData/{uid}
  const saveProgressToUserData = async (user, subjectKey, slug) => {
    const userDataRef = doc(db, "userData", user.uid);
    await setDoc(
      userDataRef,
      {
        [subjectKey]: {
          [slug]: {
            music: true,
            musicQty: increment(1),        // ✅ bump music count
            lastMusicAt: serverTimestamp() // optional
          },
        },
      },
      { merge: true }
    );
  };

  // B) mirror to classroomProgress/{educatorUid}/subjects/{subjectKey}/students/{studentUid}
  //
  // Was a where("role","in",["educator","nc-1st-educator","administrator"])
  // query — a list against /users that students aren't permitted to run and
  // that matched no real educator role anyway. Silent `return` on empty meant
  // song plays never reached the classroom. See utils/resolveEducatorUid.js.
  const mirrorToEducator = async (studentUid, subjectKey, slug) => {
    const studentSnap = await getDoc(doc(db, "users", studentUid));
    const studentDoc = studentSnap.data() || {};
    const educatorCode = studentDoc.educatorCode;
    if (!educatorCode) return;

    const educatorUid = await resolveEducatorUid(db, educatorCode);
    if (!educatorUid) return;

    const mirrorRef = doc(
      db,
      "classroomProgress",
      educatorUid,
      "subjects",
      subjectKey,
      "students",
      studentUid
    );

    await setDoc(
      mirrorRef,
      {
        studentUid,
        studentName: `${studentDoc.firstName || ""} ${studentDoc.lastName || ""}`.trim(),
        firstName: studentDoc.firstName || "",
        lastName: studentDoc.lastName || "",
        avatarUrl: studentDoc.avatarUrl || "",
        educatorCode,
        slugs: {
          [slug]: {
            music: true,
            musicQty: increment(1),
            lastMusicAt: serverTimestamp(),
          },
        },
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const markProgress = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user?.uid) return;

      await saveProgressToUserData(user, subjectKey, slug);
      await mirrorToEducator(user.uid, subjectKey, slug);

      // 🪙 First time finishing this topic's song pays coins (once per
      // topic, decided by the server). Not awaited — never delays the star.
      awardFirstPlayCoins({ kind: "music", slug, title });

      if (onProgressUpdate) {
        await onProgressUpdate();
      }
    } catch (error) {
      console.error("Error marking progress:", error);
    }
  };

  const handleConfirm = async () => {
    await markProgress();
    setShowPopup(false);
  };

  return (
    <>
      {lyricSync ? (
        <SingAlongPlayer
          musicUrl={musicUrl}
          sync={lyricSync}
          title={title}
          onEnded={() => setShowPopup(true)}
        />
      ) : (
        <div style={audioOuterStyle}>
          <audio
            controls
            src={musicUrl}
            style={audioStyle}
            onEnded={() => setShowPopup(true)}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      )}

      {showPopup && (
        <div style={overlayStyle}>
          <div style={popupStyle}>
            {/* X button absolutely positioned at top-right */}
            <button
              onClick={() => setShowPopup(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "transparent",
                border: "none",
                fontSize: "20px",
                cursor: "pointer"
              }}
            >
              ✖
            </button>

            {/* audio player with margin-top so it doesn’t overlap X */}
            <div style={audioWrapperStyle}>
              <audio
                controls
                src={musicUrl}
                style={{ width: "100%" }}
                onEnded={() => setShowPopup(true)}
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>

            <SpinningStar size={130} />
            <p style={{ marginBottom: 16, fontSize: "20px", fontWeight: 800 }}>
              Congratulations! You've earned your Music Star!
            </p>
            <button onClick={handleConfirm} style={buttonStyle}>Continue</button>
          </div>
        </div>
      )}
    </>
  );
};

const overlayStyle = {
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const audioWrapperStyle = {
  marginTop: "40px",   // pushes the audio down inside the popup
  marginBottom: "20px",
  width: "100%",
  display: "flex",
  justifyContent: "center"
};

const popupStyle = {
  position: "relative",  // ✅ so the X button anchors to this box
  background: "#fff",
  padding: "40px 20px 30px", // extra top padding for the X
  borderRadius: "12px",
  textAlign: "center",
  maxWidth: "300px",
  boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
};

const buttonStyle = {
  margin: "8px",
  padding: "10px 18px",
  backgroundColor: "#2196f3",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  cursor: "pointer",
};

const audioOuterStyle = {
  marginTop: 16,              // a little breathing room
  marginBottom: 16,
  display: "flex",
  justifyContent: "center",
};

const audioStyle = {
  width: 280,                 // smaller player
  maxWidth: "90%",            // still responsive
  display: "block",
  transform: "scale(0.95)",   // tiny extra shrink
  transformOrigin: "top center",
};

export default MusicTool;
